import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'

import { Button, buttonClasses } from '@/design-system/Button'
import { Card, Container, Overline, Skeleton } from '@/design-system/primitives'
import { PublicLayout } from '@/layouts/PublicLayout'
import { ApiError, apiClient } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useRubrics } from '../useRubrics'

type Kind = 'access' | 'rectification' | 'deletion' | 'portability'

/** L'ordre des choix : du plus courant au plus lourd de conséquences. */
const KINDS: Kind[] = ['access', 'portability', 'rectification', 'deletion']

/** Les rubriques du dossier, dans l'ordre où le serveur les rend. */
const DOSSIER_SECTIONS = [
  'customer',
  'orders',
  'attendees',
  'candidatures',
  'table_requests',
  'jury',
  'consents',
  'notifications',
] as const

interface VerifiedRequest {
  id: string
  kind: Kind
  status: string
  dossier_available: boolean
  message: string
}

type Dossier = Record<(typeof DOSSIER_SECTIONS)[number], unknown> & {
  email: string
  generated_at: string
}

/** Le plafond du serveur : au-delà, la demande serait refusée en bloc. */
const DETAILS_MAX = 2000

/**
 * « Mes données » — exercer ses droits RGPD sans créer de compte.
 *
 * Deux visages pour une même adresse :
 *
 * * **sans jeton**, le formulaire de demande. La réponse est toujours la même,
 *   que l'adresse soit connue ou non : dire « inconnue » ferait de ce
 *   formulaire un moyen de savoir qui a acheté un billet ;
 * * **avec `?token=`**, l'atterrissage du lien reçu par e-mail. Le clic prouve
 *   que la demande vient bien du titulaire de la boîte — sans lui, n'importe
 *   qui pourrait demander l'effacement des données de n'importe qui.
 *
 * La page vit hors des rubriques et ne dépend pas d'une édition publiée : un
 * droit ne s'éteint pas entre deux soirées.
 */
export function MyDataPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { event, navItems } = useRubrics()

  useEffect(() => {
    document.title = t('myData.title')
  }, [t])

  return (
    <PublicLayout
      {...(event
        ? { eventName: event.name, logoUrl: event.logo_url, availableLocales: event.locales }
        : {})}
      navItems={navItems}
    >
      <Container className="max-w-3xl py-24">
        <Overline>{t('myData.overline')}</Overline>
        <h1 className="mt-3 font-display text-display-sm text-content-primary">
          {t('myData.title')}
        </h1>
        {token ? <VerifiedView token={token} /> : <RequestForm />}
      </Container>
    </PublicLayout>
  )
}

/* ------------------------------------------------------------------ */
/* Sans jeton : expliquer, puis recueillir la demande.                 */
/* ------------------------------------------------------------------ */

function RequestForm() {
  const { t, i18n } = useTranslation()
  const [email, setEmail] = useState('')
  const [kind, setKind] = useState<Kind>('access')
  const [details, setDetails] = useState('')

  const submit = useMutation({
    mutationFn: () =>
      apiClient.post<{ message: string }>('/public/data-requests', {
        email: email.trim(),
        kind,
        // La langue de l'e-mail de confirmation, et celle dans laquelle
        // l'équipe répondra.
        locale: i18n.language,
        details: details.trim() || null,
      }),
  })

  if (submit.isSuccess) {
    // Message rédigé ici plutôt que repris du serveur : il doit être dans la
    // langue du visiteur, et il reste neutre — connue ou non, l'adresse
    // reçoit la même réponse.
    return (
      <Card className="mt-10">
        <p className="font-display text-heading-md text-content-primary">
          {t('myData.sentTitle')}
        </p>
        <p className="mt-3 text-content-secondary">{t('myData.sentBody')}</p>
        <p className="mt-3 text-sm text-content-muted">{t('myData.sentHelp')}</p>
      </Card>
    )
  }

  const rectification = kind === 'rectification'
  const errorMessage = !submit.isError
    ? null
    : submit.error instanceof ApiError && submit.error.status === 429
      ? t('myData.rateLimited')
      : submit.error instanceof ApiError && submit.error.status === 422
        ? t('myData.invalid')
        : t('myData.error')

  return (
    <>
      <p className="mt-5 text-body-lg text-content-secondary">{t('myData.intro')}</p>

      {/* Les droits en clair, avant le formulaire : on choisit mieux ce
          qu'on demande quand on sait ce que chaque droit permet. */}
      <section className="mt-10">
        <h2 className="font-display text-heading-md text-content-primary">
          {t('myData.rightsTitle')}
        </h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {KINDS.map((value) => (
            <li key={value}>
              <Card className="h-full p-5">
                <p className="text-content-primary">{t(`myData.kind.${value}.title`)}</p>
                <p className="mt-1.5 text-sm text-content-secondary">
                  {t(`myData.kind.${value}.explain`)}
                </p>
              </Card>
            </li>
          ))}
        </ul>
      </section>

      <form
        className="mt-12 space-y-8"
        onSubmit={(formEvent) => {
          formEvent.preventDefault()
          submit.mutate()
        }}
      >
        <h2 className="font-display text-heading-md text-content-primary">
          {t('myData.formTitle')}
        </h2>

        <label className="block text-caption text-content-muted">
          {t('myData.email')} *
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(changeEvent) => setEmail(changeEvent.target.value)}
            className="mt-1 w-full rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary"
          />
          <span className="mt-1 block text-xs text-content-muted">{t('myData.emailHint')}</span>
        </label>

        <fieldset className="space-y-3">
          <legend className="overline text-content-muted">{t('myData.kindLegend')}</legend>
          {KINDS.map((value) => (
            <label
              key={value}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3',
                kind === value ? 'border-champagne-400' : 'border-line',
              )}
            >
              <input
                type="radio"
                name="kind"
                value={value}
                checked={kind === value}
                onChange={() => setKind(value)}
                className="mt-1 h-4 w-4 shrink-0 accent-[var(--ember-500)]"
              />
              <span className="min-w-0">
                <span className="block text-content-primary">
                  {t(`myData.kind.${value}.title`)}
                </span>
                <span className="mt-0.5 block text-sm text-content-secondary">
                  {t(`myData.kind.${value}.after`)}
                </span>
              </span>
            </label>
          ))}
        </fieldset>

        {/* Toujours visible : une précision aide aussi pour un effacement
            (« seulement ma candidature »). Mais pour une rectification, sans
            elle l'équipe ne sait pas quoi corriger — d'où le libellé qui
            change et l'obligation. */}
        <label className="block text-caption text-content-muted">
          {rectification ? `${t('myData.detailsRectification')} *` : t('myData.details')}
          <textarea
            value={details}
            rows={rectification ? 5 : 3}
            required={rectification}
            maxLength={DETAILS_MAX}
            onChange={(changeEvent) => setDetails(changeEvent.target.value)}
            className="mt-1 w-full rounded-md border border-line bg-bg-base px-3 py-2 text-sm text-content-primary"
          />
          <span className="mt-1 block text-xs text-content-muted">
            {rectification ? t('myData.detailsRectificationHint') : t('myData.detailsHint')}
          </span>
        </label>

        {errorMessage ? (
          <p role="alert" className="text-sm text-state-danger">
            {errorMessage}
          </p>
        ) : null}

        <Button type="submit" size="lg" disabled={submit.isPending}>
          {submit.isPending ? t('myData.sending') : t('myData.submit')}
        </Button>
      </form>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Avec jeton : confirmer, puis remettre le dossier ou rassurer.       */
/* ------------------------------------------------------------------ */

function VerifiedView({ token }: { token: string }) {
  const { t } = useTranslation()

  // Une requête plutôt qu'une mutation lancée dans un effet : React rejoue
  // les effets en développement, et la confirmation partirait deux fois.
  // Le serveur l'accepte (un lien rouvert reste valable), mais l'équipe
  // n'a pas à recevoir deux alertes pour un seul clic.
  const verify = useQuery({
    queryKey: ['data-request-verify', token],
    queryFn: () => apiClient.post<VerifiedRequest>('/public/data-requests/verify', { token }),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  if (verify.isPending) {
    return (
      <div className="mt-10 space-y-4">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-32 w-full" />
        <span className="sr-only">{t('state.loading')}</span>
      </div>
    )
  }

  if (verify.isError) {
    const invalid = verify.error instanceof ApiError && verify.error.status === 404
    return (
      <div className="mt-10">
        <p className="font-display text-heading-md text-content-primary">
          {invalid ? t('myData.invalidLink') : t('myData.error')}
        </p>
        <p className="mt-3 text-content-secondary">
          {invalid ? t('myData.invalidLinkHelp') : t('myData.errorHelp')}
        </p>
        <Link to="/mes-donnees" className={buttonClasses({ variant: 'outline', className: 'mt-8' })}>
          {t('myData.backToForm')}
        </Link>
      </div>
    )
  }

  const request = verify.data

  if (request.dossier_available) return <DossierDownload token={token} />

  if (request.status === 'rejected') {
    return (
      <p className="mt-8 text-content-secondary">{t('myData.rejected')}</p>
    )
  }

  if (request.status === 'done') {
    return (
      <p className="mt-8 text-content-secondary">{t(`myData.done.${request.kind}`)}</p>
    )
  }

  // Effacement et rectification : un humain s'en charge, dans le délai légal.
  return (
    <Card className="mt-10">
      <p className="font-display text-heading-md text-content-primary">
        {t('myData.transmittedTitle')}
      </p>
      <p className="mt-3 text-content-secondary">{t(`myData.transmitted.${request.kind}`)}</p>
      <p className="mt-3 text-sm text-content-muted">{t('myData.legalDelay')}</p>
    </Card>
  )
}

/**
 * Le dossier d'accès, en JSON.
 *
 * JSON et non PDF : c'est ce que demande la portabilité — un format qu'un
 * autre service peut relire sans nous. On le télécharge plutôt que de
 * l'afficher : c'est un fichier qu'on garde, pas une page qu'on lit.
 */
function DossierDownload({ token }: { token: string }) {
  const { t } = useTranslation()
  const [summary, setSummary] = useState<Dossier | null>(null)

  const download = useMutation({
    mutationFn: () =>
      apiClient.get<Dossier>(`/public/data-requests/dossier?token=${encodeURIComponent(token)}`),
    onSuccess: (dossier) => {
      const blob = new Blob([JSON.stringify(dossier, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `leyamo-mes-donnees-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      // Libéré au tour suivant : certains navigateurs mobiles lisent l'URL
      // après le clic, et une révocation immédiate y donne un fichier vide.
      window.setTimeout(() => URL.revokeObjectURL(url), 1000)
      setSummary(dossier)
    },
  })

  const countOf = (value: unknown): number =>
    Array.isArray(value) ? value.length : value ? 1 : 0

  return (
    <div className="mt-10 space-y-6">
      <Card>
        <p className="font-display text-heading-md text-content-primary">
          {t('myData.dossierTitle')}
        </p>
        <p className="mt-3 text-content-secondary">{t('myData.dossierBody')}</p>
        <Button
          size="lg"
          className="mt-6 h-auto min-h-14 w-full min-w-0 whitespace-normal py-3 sm:w-auto"
          disabled={download.isPending}
          onClick={() => download.mutate()}
        >
          {download.isPending ? t('myData.downloading') : t('myData.download')}
        </Button>
        {download.isError ? (
          <p role="alert" className="mt-4 text-sm text-state-danger">
            {download.error instanceof ApiError && download.error.status === 404
              ? t('myData.invalidLinkHelp')
              : t('myData.error')}
          </p>
        ) : null}
      </Card>

      <section>
        <h2 className="font-display text-heading-sm text-content-primary">
          {t('myData.sectionsTitle')}
        </h2>
        <ul className="mt-3 space-y-2">
          {DOSSIER_SECTIONS.map((section) => (
            <li key={section} className="flex items-baseline justify-between gap-4 text-sm">
              <span className="text-content-secondary">{t(`myData.sections.${section}`)}</span>
              {/* Les nombres n'apparaissent qu'une fois le dossier reçu :
                  on montre ce qu'il contient vraiment, pas ce qu'il devrait
                  contenir. */}
              {summary ? (
                <span className="shrink-0 tabular-nums text-content-primary">
                  {countOf(summary[section])}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <p className="text-sm text-content-muted">
        {t('myData.otherRequest')}{' '}
        <Link to="/mes-donnees" className="text-champagne-300 underline">
          {t('myData.backToForm')}
        </Link>
      </p>
    </div>
  )
}
